import { Container, Graphics, Sprite } from 'pixi.js';
import type { PublicPlayerAppearance } from '@push.io/shared';
import { warnCosmeticFallback } from '../fallback-warning.js';
import { AssetManager } from '../AssetManager.js';

const PATTERN_COLOR = 0xffffff;

export class PatternLayer {
  readonly container: Container;
  private gfx: Graphics;
  private maskGfx: Graphics;
  private sprite: Sprite | null = null;
  private currentPattern: string | null = null;
  private currentRadius: number = 0;

  constructor() {
    this.container = new Container();
    this.gfx = new Graphics();
    this.maskGfx = new Graphics();
    this.container.addChild(this.gfx);
    this.container.addChild(this.maskGfx);
    this.container.mask = this.maskGfx;
    this.container.alpha = 0.35;
  }

  update(appearance: PublicPlayerAppearance | null, radius: number): void {
    const patternId = appearance?.patternId ?? null;
    if (patternId === this.currentPattern && radius === this.currentRadius) return;

    this.currentPattern = patternId;
    this.currentRadius = radius;
    this.gfx.clear();
    this.detachSprite();

    if (!patternId) {
      this.container.visible = false;
      return;
    }

    this.container.visible = true;

    this.maskGfx.clear();
    this.maskGfx.circle(0, 0, radius);
    this.maskGfx.fill({ color: 0xffffff });

    const texture = AssetManager.getPatternTexture(patternId);
    if (texture) {
      this.sprite = new Sprite(texture);
      this.sprite.anchor.set(0.5);
      this.sprite.position.set(0, 0);
      this.sprite.width = radius * 2;
      this.sprite.height = radius * 2;
      this.container.addChildAt(this.sprite, 0);
      return;
    }

    const g = this.gfx;
    const s = radius;

    switch (patternId) {
      case 'cosm_pattern_stripes': {
        for (let i = -s; i < s; i += 4) {
          g.rect(i, -s, 1.5, s * 2);
          g.fill({ color: PATTERN_COLOR, alpha: 0.10 });
        }
        break;
      }
      case 'cosm_pattern_rings': {
        for (let r = s * 0.25; r < s; r += s * 0.22) {
          g.circle(0, 0, r);
          g.stroke({ width: 2, color: PATTERN_COLOR, alpha: 0.12 });
        }
        break;
      }
      case 'cosm_pattern_dots': {
        const spacing = s * 0.35;
        for (let x = -s; x < s; x += spacing) {
          for (let y = -s; y < s; y += spacing) {
            const dist = Math.hypot(x, y);
            if (dist < s * 0.85) {
              g.circle(x, y, 1.8);
              g.fill({ color: PATTERN_COLOR, alpha: 0.11 });
            }
          }
        }
        break;
      }
      default: {
        warnCosmeticFallback('pattern', patternId);
        this.container.visible = false;
        break;
      }
    }
  }

  setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  destroy(): void {
    this.gfx.clear();
    this.gfx.destroy();
    this.detachSprite();
    this.maskGfx.clear();
    this.maskGfx.destroy();
    this.container.removeChildren();
  }

  private detachSprite(): void {
    if (!this.sprite) return;
    this.container.removeChild(this.sprite);
    this.sprite.destroy({ texture: false });
    this.sprite = null;
  }
}
